<template>
	<div class="theme-selector">
		<v-menu :close-on-content-click="false">
			<template v-slot:activator="{ props }">
				<v-btn
					variant="text"
					class="theme-btn"
					v-bind="props"
				>
					<v-icon start>mdi-palette</v-icon>
					<span v-if="!mobile" class="theme-text">
						{{ $t("themes.title") }}
					</span>
				</v-btn>
			</template>

			<v-card min-width="280" max-width="320" class="pa-4">
				<div class="d-flex align-center mb-3">
					<v-icon class="me-2" size="small">mdi-palette</v-icon>
					<span class="text-subtitle-1 font-weight-medium">
						{{ $t("themes.title") }}
					</span>
				</div>

				<!-- ORI theme - featured item -->
				<div
					class="ori-theme-item d-flex align-center pa-2 rounded-lg mb-3"
					:class="{ active: currentThemeName === 'ori' }"
					@click="selectTheme('ori')"
				>
					<div
						class="theme-swatch"
						:style="{ backgroundColor: oriThemeColors.primary }"
					>
						<div
							class="theme-swatch-inner"
							:style="{
								backgroundColor: oriThemeColors.secondary,
							}"
						/>
					</div>
					<span class="ms-3 font-weight-medium">ORI</span>
					<v-spacer />
					<v-icon
						v-if="currentThemeName === 'ori'"
						size="small"
						color="primary"
					>
						mdi-check
					</v-icon>
				</div>

				<!-- Other themes - swatch grid -->
				<div class="d-flex flex-wrap gap-3 mb-3">
					<v-tooltip
						v-for="theme in otherThemes"
						:key="theme.name"
						:text="$t(`themes.orixas.${theme.name}`)"
						location="bottom"
					>
						<template v-slot:activator="{ props: tooltipProps }">
							<div
								v-bind="tooltipProps"
								class="theme-swatch"
								:class="{
									active: currentThemeName === theme.name,
								}"
								:style="{
									backgroundColor: theme.colors.primary,
								}"
								@click="selectTheme(theme.name)"
							>
								<div
									class="theme-swatch-inner"
									:style="{
										backgroundColor:
											theme.colors.secondary,
									}"
								/>
							</div>
						</template>
					</v-tooltip>
				</div>

				<v-divider class="mb-3" />

				<!-- Dark mode toggle -->
				<div class="d-flex align-center justify-space-between">
					<div class="d-flex align-center">
						<v-icon class="me-2" size="small">
							mdi-weather-night
						</v-icon>
						<span class="text-body-2">
							{{ $t("themes.darkMode") }}
						</span>
					</div>
					<v-switch
						:model-value="isDark"
						@update:model-value="toggleDark"
						color="primary"
						density="compact"
						hide-details
					/>
				</div>
			</v-card>
		</v-menu>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useDisplay } from "vuetify";
import { useOrixaTheme } from "@/composables/useOrixaTheme";
import { useI18n } from "vue-i18n";

const { mobile } = useDisplay();
const { t } = useI18n();
const {
	currentThemeName,
	currentTheme,
	themes,
	changeTheme,
	toggleDark,
	isDark,
} = useOrixaTheme();

const oriThemeColors = computed(() => {
	const ori = themes.value.find((t) => t.name === "ori");
	return ori?.colors ?? currentTheme.value.colors;
});

const otherThemes = computed(() =>
	themes.value.filter((t) => t.name !== "ori")
);

const selectTheme = (themeName: string) => {
	changeTheme(themeName);
};
</script>

<style scoped>
.theme-btn {
	min-width: auto;
	letter-spacing: normal;
}

.theme-text {
	font-weight: 500;
}

/* Bicolor icon in header button */
.theme-swatch-icon {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.theme-swatch-icon-inner {
	width: 12px;
	height: 12px;
	border-radius: 50%;
}

/* Swatch circles */
.theme-swatch {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: transform 0.15s;
	border: 2px solid transparent;
	flex-shrink: 0;
}

.theme-swatch:hover {
	transform: scale(1.15);
}

.theme-swatch.active {
	border-color: rgb(var(--v-theme-on-surface));
}

.theme-swatch-inner {
	width: 20px;
	height: 20px;
	border-radius: 50%;
}

/* ORI featured item */
.ori-theme-item {
	cursor: pointer;
	transition: background-color 0.15s;
}

.ori-theme-item:hover {
	background-color: rgba(var(--v-theme-primary), 0.08);
}

.ori-theme-item.active {
	background-color: rgba(var(--v-theme-primary), 0.12);
}
</style>
